/** Daily recurrence — multiple runs per day (client) */

import dayjs from "dayjs";

export const DEFAULT_RECURRING_RESET_TIME = "11:50 pm";
export const DEFAULT_INTERVAL_START_TIME = "9:00 am";

export const parseTimeString = (str) => {
  const input = String(str || DEFAULT_RECURRING_RESET_TIME).trim().toLowerCase();
  const m = input.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!m) return { hour: 9, minute: 0 };
  let hour = Number(m[1]) % 12;
  const minute = Number(m[2]);
  if (m[3] === "pm") hour += 12;
  return { hour, minute };
};

export const formatTimeString = (hour, minute) => {
  const h24 = ((hour % 24) + 24) % 24;
  const period = h24 < 12 ? "am" : "pm";
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(minute).padStart(2, "0")} ${period}`;
};

export const normalizeTimeString = (str) => {
  const { hour, minute } = parseTimeString(str);
  return formatTimeString(hour, minute);
};

export const timeToMinutes = (str) => {
  const { hour, minute } = parseTimeString(str);
  return hour * 60 + minute;
};

export const sortTimeStrings = (times) =>
  [...times].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));

export const generateIntervalTimes = (config) => {
  const runs = Math.max(1, Math.min(24, Number(config.dailyIntervalRunsPerDay) || 2));
  const hours = Math.max(1, Math.min(23, Number(config.dailyIntervalHours) || 1));
  const start = parseTimeString(
    config.dailyIntervalStartTime || DEFAULT_INTERVAL_START_TIME
  );
  const out = [];
  let totalMinutes = start.hour * 60 + start.minute;
  for (let i = 0; i < runs; i += 1) {
    if (totalMinutes >= 24 * 60) break;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    out.push(formatTimeString(h, m));
    totalMinutes += hours * 60;
  }
  return out.length ? out : [formatTimeString(start.hour, start.minute)];
};

export const isDailySlotsMode = (config) =>
  config &&
  (config.cadence || "daily") === "daily" &&
  (config.dailyRunMode || "single") === "slots";

export const isDailyIntervalMode = (config) =>
  config &&
  (config.cadence || "daily") === "daily" &&
  (config.dailyRunMode || "single") === "interval";

export const isDailyMultiCheckpointMode = (config) =>
  isDailySlotsMode(config) || isDailyIntervalMode(config);

export const getDailyCheckpointTimes = (config) => {
  if (!config || (config.cadence || "daily") !== "daily") {
    return [normalizeTimeString(config?.createTime)];
  }
  const mode = config.dailyRunMode || "single";
  if (mode === "slots") {
    const raw = Array.isArray(config.createTimes) ? config.createTimes : [];
    const normalized = sortTimeStrings(
      raw.map((t) => normalizeTimeString(t)).filter(Boolean)
    );
    if (normalized.length) return normalized;
    return [normalizeTimeString(DEFAULT_RECURRING_RESET_TIME)];
  }
  if (mode === "interval") {
    return sortTimeStrings(generateIntervalTimes(config));
  }
  return [normalizeTimeString(config.createTime)];
};

export const getDailyDayResetTime = (config) =>
  normalizeTimeString(config?.createTime || DEFAULT_RECURRING_RESET_TIME);

/** @deprecated alias */
export const getDailySlotsResetTime = getDailyDayResetTime;

/** @deprecated alias */
export const getDailySlotTimes = getDailyCheckpointTimes;

export const getDailyCheckpointScheduleTimes = (config) => {
  const checkpoints = getDailyCheckpointTimes(config);
  const reset = getDailyDayResetTime(config);
  if (!checkpoints.length) return [reset];
  const last = checkpoints[checkpoints.length - 1];
  if (timeToMinutes(reset) > timeToMinutes(last)) {
    const resetNorm = normalizeTimeString(reset);
    if (!checkpoints.some((t) => normalizeTimeString(t) === resetNorm)) {
      return [...checkpoints, resetNorm];
    }
  }
  return checkpoints;
};

/** @deprecated alias */
export const getDailySlotsScheduleTimes = getDailyCheckpointScheduleTimes;

export const getDailyCreateTimes = (config) => {
  if (!config || (config.cadence || "daily") !== "daily") {
    return [normalizeTimeString(config?.createTime)];
  }
  const mode = config.dailyRunMode || "single";
  if (mode === "slots" || mode === "interval") {
    return getDailyCheckpointTimes(config);
  }
  return [normalizeTimeString(config.createTime)];
};

export const formatDailyTimesSummary = (config) => {
  const mode = config?.dailyRunMode || "single";
  const times = getDailyCreateTimes(config);
  if (mode === "single") {
    return `Time: ${times[0] || DEFAULT_RECURRING_RESET_TIME}`;
  }
  if (mode === "interval") {
    const runs = Number(config.dailyIntervalRunsPerDay) || times.length;
    const hours = Number(config.dailyIntervalHours) || 1;
    const reset = getDailyDayResetTime(config);
    return `${runs}×/day from ${config.dailyIntervalStartTime || times[0]}, every ${hours}h (${times.join(", ")}). Reset at: ${reset}.`;
  }
  if (mode === "slots") {
    const reset = getDailyDayResetTime(config);
    return `Times: ${times.join(", ")}. Reset at: ${reset}.`;
  }
  return `Time: ${times[0] || DEFAULT_RECURRING_RESET_TIME}`;
};

export const dayjsFromTimeString = (str) => {
  const { hour, minute } = parseTimeString(str);
  return dayjs().hour(hour).minute(minute).second(0);
};

export const isDailyCheckpointResetAfterLastCheckpoint = (config) => {
  if (!isDailyMultiCheckpointMode(config)) return true;
  const checkpoints = getDailyCheckpointTimes(config);
  if (!checkpoints.length) return true;
  const last = checkpoints[checkpoints.length - 1];
  const reset = getDailyDayResetTime(config);
  return timeToMinutes(reset) > timeToMinutes(last);
};

/** @deprecated alias */
export const isDailySlotsResetAfterLastSlot = isDailyCheckpointResetAfterLastCheckpoint;
